import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'error') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type }]);

    // Only auto-dismiss success/info — errors stay until user clicks ×
    if (type === 'success' || type === 'info') {
      timers.current[id] = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
        delete timers.current[id];
      }, 4000);
    }
  }, []);

  const toast = useCallback((type) => (msg) => addToast(msg, type), [addToast]);

  const value = {
    error:   (msg) => addToast(msg, 'error'),
    success: (msg) => addToast(msg, 'success'),
    info:    (msg) => addToast(msg, 'info'),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Portal-like fixed container outside normal flow */}
      <div
        aria-live="assertive"
        style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
                 display: 'flex', flexDirection: 'column', gap: '10px',
                 width: '340px', pointerEvents: 'none' }}
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const STYLES = {
  error: {
    bg: '#fef2f2', border: '#fca5a5', color: '#991b1b',
    iconBg: '#fee2e2', iconColor: '#dc2626', icon: '✕', label: 'Error',
  },
  success: {
    bg: '#f0fdf4', border: '#86efac', color: '#166534',
    iconBg: '#dcfce7', iconColor: '#16a34a', icon: '✓', label: 'Success',
  },
  info: {
    bg: '#eff6ff', border: '#93c5fd', color: '#1e40af',
    iconBg: '#dbeafe', iconColor: '#2563eb', icon: 'i', label: 'Info',
  },
};

const ToastItem = ({ toast, onClose }) => {
  const s = STYLES[toast.type] || STYLES.error;
  const isAutoDismiss = toast.type === 'success' || toast.type === 'info';

  return (
    <div
      className="toast-enter"
      style={{
        pointerEvents: 'auto',
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px' }}>
        {/* Icon */}
        <div style={{
          flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%',
          backgroundColor: s.iconBg, color: s.iconColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '12px', fontWeight: 700,
        }}>
          {s.icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.05em', opacity: 0.6, margin: '0 0 3px 0' }}>
            {s.label}
          </p>
          <p style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.4, margin: 0 }}>
            {toast.message}
          </p>
          {!isAutoDismiss && (
            <p style={{ fontSize: '11px', opacity: 0.5, marginTop: '4px', marginBottom: 0 }}>
              Click × to dismiss
            </p>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '20px', lineHeight: 1, opacity: 0.4, padding: '0 2px',
            color: s.color, marginTop: '-2px',
          }}
          onMouseEnter={e => e.target.style.opacity = 0.9}
          onMouseLeave={e => e.target.style.opacity = 0.4}
        >
          ×
        </button>
      </div>

      {/* Progress bar for auto-dismiss */}
      {isAutoDismiss && (
        <div style={{ height: '3px', backgroundColor: 'rgba(0,0,0,0.08)' }}>
          <div
            className="toast-progress"
            style={{ height: '100%', backgroundColor: s.iconColor, opacity: 0.5 }}
          />
        </div>
      )}
    </div>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};
